import "./PlanThemes.scss";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { MdArrowDownward, MdArrowUpward, MdClose } from "react-icons/md";
import { toast } from "react-toastify";

import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import api from "../../services/api";
import PlansLook from "./PlansLook";
import PlanStoryText from "./PlanStoryText";

// Plan Themes: the backgrounds and title fonts the app's plan designer offers.
// The app reads this catalog at start, so changes here need no app build.
const KINDS = [
  { value: "gradient", label: "Gradient" },
  { value: "shader", label: "Shader" },
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
];
const INTERACTIONS = [
  { value: "tap", label: "Tap (blooms at the touch, steps the hue if set)" },
  { value: "track", label: "Track (light follows the finger)" },
  { value: "none", label: "None" },
];
const DEFAULT_SKSL = `uniform vec2 u_res; uniform float u_time;
vec4 main(vec2 fragCoord){
  vec2 uv = fragCoord / u_res;
  return vec4(uv.x, uv.y, 0.5 + 0.5 * sin(u_time), 1.0);
}`;
const SAMPLE_TITLE = "Saturday at the Loft";

const EMPTY_THEME = {
  key: "",
  name: "",
  kind: "gradient",
  payload: { colors: ["#0a3fd6", "#7229f5"], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } },
  swatch_url: "",
  tone: "dark",
  ring_colors: [],
  join_pill: { bg: "", fg: "" },
  title_font: "",
  availability: "all",
  active: true,
};
const EMPTY_FONT = {
  key: "",
  name: "",
  file_url: "",
  license: { name: "OFL-1.1", url: "" },
  weight: "400",
  style: "normal",
  title: { font_size: "", line_height: "", letter_spacing: "", max_lines: "", uppercase: false },
  picker_size: "",
  active: true,
};

const errMsg = (err, fallback) => err?.response?.data?.message || fallback;

// Mirrors the server's uniform check so a bad shader is caught before saving.
function shaderProblems(src, uniforms) {
  const code = String(src || "").replace(/\/\*[\s\S]*?\*\//g, " ").replace(/\/\/[^\n]*/g, " ");
  const out = [];
  if (new Blob([src || ""]).size > 20 * 1024) out.push("Over 20KB.");
  if (!/\bvec4\s+main\s*\(\s*(?:float2|vec2)\s+\w+\s*\)/.test(code)) out.push("Needs vec4 main(vec2 fragCoord).");
  const re = /\buniform\s+(\w+)\s+([^;]+);/g;
  let m;
  while ((m = re.exec(code))) {
    for (const part of m[2].split(",")) {
      const name = part.trim();
      const want = uniforms[name];
      const type = m[1] === "float2" ? "vec2" : m[1];
      if (!want) out.push(`${name} is not a uniform the app supplies.`);
      else if (want !== type) out.push(`${name} must be a ${want}.`);
    }
  }
  return out;
}

function gradientCss(p) {
  const colors = (p?.colors || []).filter(Boolean);
  if (colors.length < 2) return "#15151c";
  const s = p.start || { x: 0, y: 0 };
  const e = p.end || { x: 1, y: 1 };
  // CSS angles run clockwise from "to top"; screen y grows downward.
  const deg = (Math.atan2(e.x - s.x, -(e.y - s.y)) * 180) / Math.PI;
  return `linear-gradient(${Math.round(deg)}deg, ${colors.join(", ")})`;
}

// A plan-shaped preview of a theme. Shaders only render in the app.
function ThemePreview({ theme, fallbackSwatch, small }) {
  const p = theme.payload || {};
  const cls = `pt__preview${small ? " pt__preview--small" : ""}`;
  const scrim = Number(p.scrim) > 0 ? <div className="pt__scrim" style={{ opacity: Number(p.scrim) }} /> : null;
  const ink = theme.tone === "light" ? "#0a0a0f" : "#fff";
  const title = small ? null : <div className="pt__preview-title" style={{ color: ink }}>{SAMPLE_TITLE}</div>;
  if (theme.kind === "gradient") {
    return <div className={cls} style={{ background: gradientCss(p) }}>{title}</div>;
  }
  if (theme.kind === "image" && p.image_url) {
    return <div className={cls} style={{ backgroundImage: `url(${p.image_url})` }}>{scrim}{title}</div>;
  }
  if (theme.kind === "video" && p.video_url) {
    return (
      <div className={cls}>
        <video className="pt__video" src={p.video_url} poster={p.poster_url || undefined} muted loop autoPlay playsInline />
        {scrim}
        {title}
      </div>
    );
  }
  const still = theme.swatch_url || fallbackSwatch;
  return (
    <div className={cls} style={still ? { backgroundImage: `url(${still})` } : undefined}>
      {!still && <span className="pt__preview-note">{theme.kind === "shader" ? "Shader: previews in the app" : "No preview"}</span>}
      {title}
    </div>
  );
}

// One page-wide family name per font file URL.
function familyFor(url) {
  let h = 5381;
  for (let i = 0; i < url.length; i += 1) h = ((h * 33) ^ url.charCodeAt(i)) >>> 0;
  return `plan-${h.toString(36)}`;
}

// Loads a catalog font into the page so its name can be set in its own face.
function useWebFont(url) {
  const [state, setState] = useState("idle");
  useEffect(() => {
    if (!url || typeof FontFace === "undefined") { setState("idle"); return undefined; }
    let live = true;
    setState("loading");
    const face = new FontFace(familyFor(url), `url(${url})`);
    face.load().then((f) => {
      document.fonts.add(f);
      if (live) setState("ready");
    }).catch(() => { if (live) setState("failed"); });
    return () => { live = false; };
  }, [url]);
  return state;
}

function FontSample({ font, text, size }) {
  const state = useWebFont(font.file_url);
  const ready = state === "ready";
  const t = font.title || {};
  return (
    <span
      className="pt__font-sample"
      style={ready ? {
        fontFamily: familyFor(font.file_url),
        fontSize: size || Number(t.font_size) || 32,
        lineHeight: t.line_height ? `${t.line_height}px` : 1.15,
        letterSpacing: Number(t.letter_spacing) || 0,
        textTransform: t.uppercase ? "uppercase" : "none",
      } : { fontSize: size || 18, color: "#8a8a99" }}>
      {ready ? text : state === "failed" ? "Preview unavailable here; the app loads it" : "Loading font..."}
    </span>
  );
}

async function uploadAsset(file) {
  const body = new FormData();
  body.append("file", file);
  const { data } = await api.post("/admin/plan-themes/assets", body);
  return data.url;
}

function UploadButton({ label, accept, onUploaded }) {
  const [busy, setBusy] = useState(false);
  return (
    <label className="pt__upload">
      {busy ? "Uploading..." : label}
      <input
        type="file"
        accept={accept}
        disabled={busy}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (!file) return;
          setBusy(true);
          try {
            onUploaded(await uploadAsset(file));
            toast.success("Uploaded");
          } catch (err) {
            toast.error(errMsg(err, "Could not upload the file"));
          } finally {
            setBusy(false);
          }
        }}
      />
    </label>
  );
}

function ColorList({ value, onChange, min, max }) {
  const list = value || [];
  return (
    <div className="pt__colors">
      {list.map((c, i) => (
        <span key={i} className="pt__color">
          <input type="color" value={c} onChange={(e) => onChange(list.map((x, j) => (j === i ? e.target.value : x)))} />
          <input className="pt__hex" value={c} onChange={(e) => onChange(list.map((x, j) => (j === i ? e.target.value : x)))} />
          {list.length > min && (
            <button type="button" className="pt__link" onClick={() => onChange(list.filter((_, j) => j !== i))}>Remove</button>
          )}
        </span>
      ))}
      {list.length < max && (
        <button type="button" className="pt__link" onClick={() => onChange([...list, list[list.length - 1] || "#ffffff"])}>Add color</button>
      )}
    </div>
  );
}

function moveKey(list, index, delta) {
  const next = list.slice();
  const [item] = next.splice(index, 1);
  next.splice(index + delta, 0, item);
  return next;
}

function PlanThemes() {
  const [data, setData] = useState(null);
  const [editingTheme, setEditingTheme] = useState(null);
  const [editingFont, setEditingFont] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/plan-themes");
      setData(d);
    } catch (err) {
      toast.error(errMsg(err, "Could not load plan themes"));
      setData({ themes: [], fonts: [], uniforms: {}, builtin_theme_keys: [], builtin_font_keys: [], licenses: [] });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const apiBase = process.env.REACT_APP_API_URL || "";
  // Bundled themes have baked art on the server; used when a row has no swatch.
  const builtinSwatch = useCallback(
    (key) => ((data?.builtin_theme_keys || []).includes(key) ? `${apiBase}/images/guestlist/${key}.png` : ""),
    [data, apiBase],
  );

  const reorder = async (path, keys) => {
    try {
      await api.post(path, { keys });
      load();
    } catch (err) {
      toast.error(errMsg(err, "Could not save the order"));
    }
  };

  const setActive = async (path, active) => {
    try {
      await api.post(`${path}/${active ? "restore" : "retire"}`);
      toast.success(active ? "Back in the picker" : "Retired from the picker");
      load();
    } catch (err) {
      toast.error(errMsg(err, "Could not update it"));
    }
  };

  const themes = data?.themes || [];
  const fonts = data?.fonts || [];

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="pt">
            <div className="pt__header">
              <h2 className="pt__title">Plan Themes</h2>
              <button className="pt__primary" onClick={() => setEditingTheme({ ...EMPTY_THEME })}>Add theme</button>
            </div>
            <p className="pt__hint">
              The backgrounds hosts pick in the plan designer. The app loads this list when it opens
              and keeps its bundled themes as a fallback, so an empty list still works. A theme with
              the same key as a bundled one (zona, noir, holo, trippy, wave, cyber, smoke, aura, grid,
              ripple, pink) replaces it. Retiring hides a theme from the picker; plans already using it
              keep rendering. Keys never change once saved, because plans store them.
            </p>

            <div className="pt__table-wrap">
              <table className="pt__table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th></th>
                    <th>Theme</th>
                    <th>Kind</th>
                    <th>Tone</th>
                    <th>Who sees it</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {!data && <tr><td colSpan={8} className="pt__empty">Loading...</td></tr>}
                  {data && !themes.length && (
                    <tr><td colSpan={8} className="pt__empty">No catalog themes yet. The app is using its bundled ones.</td></tr>
                  )}
                  {themes.map((t, i) => (
                    <tr key={t.key} className={t.active ? "" : "pt__row--off"}>
                      <td className="pt__order">
                        <button className="pt__icon" disabled={i === 0} aria-label="Move up"
                          onClick={() => reorder("/admin/plan-themes/reorder", moveKey(themes.map((x) => x.key), i, -1))}>
                          <MdArrowUpward />
                        </button>
                        <button className="pt__icon" disabled={i === themes.length - 1} aria-label="Move down"
                          onClick={() => reorder("/admin/plan-themes/reorder", moveKey(themes.map((x) => x.key), i, 1))}>
                          <MdArrowDownward />
                        </button>
                      </td>
                      <td><ThemePreview theme={t} fallbackSwatch={builtinSwatch(t.key)} small /></td>
                      <td>
                        <div className="pt__name">{t.name}</div>
                        <div className="pt__sub">{t.key}</div>
                      </td>
                      <td>{KINDS.find((k) => k.value === t.kind)?.label || t.kind}</td>
                      <td>{t.tone === "light" ? "Light" : "Dark"}</td>
                      <td>{t.availability === "verified" ? "Verified hosts" : "Everyone"}</td>
                      <td><span className={`pt__status pt__status--${t.active ? "on" : "off"}`}>{t.active ? "In picker" : "Retired"}</span></td>
                      <td className="pt__actions">
                        <button className="pt__link" onClick={() => setEditingTheme(t)}>Edit</button>
                        <button className="pt__link" onClick={() => setActive(`/admin/plan-themes/${t.key}`, !t.active)}>
                          {t.active ? "Retire" : "Restore"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pt__header pt__header--fonts">
              <h3 className="pt__subtitle">Title fonts</h3>
              <button className="pt__primary" onClick={() => setEditingFont({ ...EMPTY_FONT })}>Add font</button>
            </div>
            <p className="pt__hint">
              Every face in the plan title picker, in this order. Rows marked Built in are bundled in
              the app ({(data?.builtin_font_keys || []).join(", ")}): rename them, reorder them or turn
              them on and off, no file needed. A bundled face with no row here stays hidden, except Bold.
              Added fonts are downloaded by the app when a plan or the picker needs them. Only openly
              licensed fonts (OFL or similar), uploaded here unchanged: never subset or edit a font
              with a Reserved Font Name.
            </p>
            <div className="pt__table-wrap">
              <table className="pt__table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Font</th>
                    <th>Sample</th>
                    <th>License</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data && !fonts.length && (
                    <tr><td colSpan={6} className="pt__empty">No fonts yet. The app shows only Bold.</td></tr>
                  )}
                  {fonts.map((f, i) => (
                    <tr key={f.key} className={f.active ? "" : "pt__row--off"}>
                      <td className="pt__order">
                        <button className="pt__icon" disabled={i === 0} aria-label="Move up"
                          onClick={() => reorder("/admin/plan-themes/fonts/reorder", moveKey(fonts.map((x) => x.key), i, -1))}>
                          <MdArrowUpward />
                        </button>
                        <button className="pt__icon" disabled={i === fonts.length - 1} aria-label="Move down"
                          onClick={() => reorder("/admin/plan-themes/fonts/reorder", moveKey(fonts.map((x) => x.key), i, 1))}>
                          <MdArrowDownward />
                        </button>
                      </td>
                      <td>
                        <div className="pt__name">{f.name}</div>
                        <div className="pt__sub">{f.builtin ? `${f.key}, built in` : `${f.key}, ${f.weight} ${f.style}`}</div>
                      </td>
                      <td>
                        {f.builtin
                          ? <span className="pt__builtin">Built in</span>
                          : <FontSample font={f} text={SAMPLE_TITLE} size={24} />}
                      </td>
                      <td>
                        {f.builtin ? "Bundled in the app" : f.license?.url ? <a href={f.license.url} target="_blank" rel="noreferrer">{f.license.name}</a> : f.license?.name}
                      </td>
                      <td><span className={`pt__status pt__status--${f.active ? "on" : "off"}`}>{f.active ? "In picker" : f.builtin ? "Off" : "Retired"}</span></td>
                      <td className="pt__actions">
                        <button className="pt__link" onClick={() => setEditingFont(f)}>{f.builtin ? "Rename" : "Edit"}</button>
                        <button className="pt__link" onClick={() => setActive(`/admin/plan-themes/fonts/${f.key}`, !f.active)}>
                          {f.builtin ? (f.active ? "Turn off" : "Turn on") : f.active ? "Retire" : "Restore"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <PlansLook />

            <PlanStoryText />
          </section>
        </article>
      </div>

      <ThemeForm
        theme={editingTheme}
        uniforms={data?.uniforms || {}}
        fonts={fonts}
        builtinFonts={data?.builtin_font_keys || []}
        fallbackSwatch={editingTheme ? builtinSwatch(editingTheme.key) : ""}
        onClose={() => setEditingTheme(null)}
        onSaved={() => { setEditingTheme(null); load(); }}
      />
      <FontForm
        font={editingFont}
        licenses={data?.licenses || ["OFL-1.1"]}
        onClose={() => setEditingFont(null)}
        onSaved={() => { setEditingFont(null); load(); }}
      />
    </main>
  );
}

// Default payload when switching kind, so each kind starts valid-looking.
function payloadFor(kind, prev) {
  if (kind === "gradient") return prev?.colors ? prev : { colors: ["#0a3fd6", "#7229f5"], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } };
  if (kind === "shader") return prev?.sksl !== undefined ? prev : { sksl: DEFAULT_SKSL, uniforms: {}, interaction: "tap", hue_step: 0 };
  if (kind === "image") return prev?.image_url !== undefined && prev?.video_url === undefined ? prev : { image_url: "", scrim: 0 };
  return prev?.video_url !== undefined ? prev : { video_url: "", poster_url: "", scrim: 0 };
}

function ThemeForm({ theme, uniforms, fonts, builtinFonts, fallbackSwatch, onClose, onSaved }) {
  const show = !!theme;
  const isEdit = !!theme?._id;
  const [form, setForm] = useState(EMPTY_THEME);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!theme) return;
    setForm({
      ...EMPTY_THEME,
      ...theme,
      join_pill: { bg: "", fg: "", ...(theme.join_pill || {}) },
      payload: payloadFor(theme.kind || "gradient", theme.payload),
    });
  }, [theme]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setP = (k, v) => setForm((f) => ({ ...f, payload: { ...f.payload, [k]: v } }));
  const p = form.payload || {};
  const problems = useMemo(
    () => (form.kind === "shader" ? shaderProblems(p.sksl, uniforms) : []),
    [form.kind, p.sksl, uniforms],
  );

  const save = async (e) => {
    e.preventDefault();
    if (problems.length) return toast.error(problems[0]);
    const body = {
      name: form.name.trim(),
      kind: form.kind,
      payload: form.payload,
      swatch_url: form.swatch_url || "",
      tone: form.tone,
      ring_colors: form.ring_colors || [],
      join_pill: form.join_pill,
      title_font: form.title_font || "",
      availability: form.availability,
      active: !!form.active,
    };
    if (!isEdit) body.key = form.key.trim().toLowerCase();
    setSaving(true);
    try {
      if (isEdit) await api.put(`/admin/plan-themes/${theme.key}`, body);
      else await api.post("/admin/plan-themes", body);
      toast.success(isEdit ? "Theme saved" : "Theme added");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err, "Could not save the theme"));
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  const point = (which, axis, value) => setP(which, { ...(p[which] || { x: 0, y: 0 }), [axis]: Number(value) });

  return (
    <Modal show={show} onHide={onClose} centered size="xl">
      <form className="pt__form" onSubmit={save}>
        <div className="pt__form-header">
          <div className="pt__form-title">{isEdit ? `Edit ${theme?.name}` : "Add theme"}</div>
          <button type="button" className="pt__close" onClick={onClose} aria-label="Close"><MdClose size={20} /></button>
        </div>
        <div className="pt__form-split">
          <div className="pt__form-body">
            <div className="pt__inline">
              <label className="pt__field">
                <span>Name</span>
                <input value={form.name} maxLength={24} onChange={(e) => set("name", e.target.value)} placeholder="Velvet" />
              </label>
              <label className="pt__field">
                <span>Key</span>
                <input value={form.key} disabled={isEdit} maxLength={32} onChange={(e) => set("key", e.target.value)} placeholder="velvet" />
              </label>
            </div>
            <label className="pt__field">
              <span>Kind</span>
              <select value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value, payload: payloadFor(e.target.value) }))}>
                {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </label>

            {form.kind === "gradient" && (
              <>
                <div className="pt__field">
                  <span>Colors</span>
                  <ColorList value={p.colors} min={2} max={6} onChange={(v) => setP("colors", v)} />
                </div>
                <div className="pt__inline">
                  {["start", "end"].map((which) => (
                    <span key={which} className="pt__point">
                      {which === "start" ? "From" : "To"}
                      {["x", "y"].map((axis) => (
                        <label key={axis}>
                          {axis}
                          <input type="number" min={0} max={1} step={0.05} value={p[which]?.[axis] ?? 0} onChange={(e) => point(which, axis, e.target.value)} />
                        </label>
                      ))}
                    </span>
                  ))}
                </div>
              </>
            )}

            {form.kind === "shader" && (
              <>
                <label className="pt__field">
                  <span>SkSL source</span>
                  <textarea className="pt__code" rows={14} value={p.sksl || ""} onChange={(e) => setP("sksl", e.target.value)} spellCheck={false} />
                </label>
                <div className="pt__note">
                  Shaders preview in the app, not here. Upload a swatch so the picker and paused pages
                  have a still. The app fills these uniforms on every frame, and a shader may declare
                  only these: {Object.entries(uniforms).map(([k, v]) => `${v} ${k}`).join(", ")}.
                  If it fails to compile, the app shows the ZONA gradient instead.
                </div>
                {problems.length > 0 && <div className="pt__error">{problems.join(" ")}</div>}
                <div className="pt__inline">
                  <label>
                    Touch
                    <select value={p.interaction || "tap"} onChange={(e) => setP("interaction", e.target.value)}>
                      {INTERACTIONS.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                    </select>
                  </label>
                  <label>
                    Hue step per tap
                    <input type="number" min={0} max={1} step={0.01} value={p.hue_step ?? 0} onChange={(e) => setP("hue_step", Number(e.target.value))} />
                  </label>
                  <label>
                    Starting u_hue
                    <input
                      type="number"
                      step={0.01}
                      value={p.uniforms?.u_hue ?? ""}
                      onChange={(e) => {
                        const u = { ...(p.uniforms || {}) };
                        if (e.target.value === "") delete u.u_hue;
                        else u.u_hue = Number(e.target.value);
                        setP("uniforms", u);
                      }}
                    />
                  </label>
                </div>
              </>
            )}

            {(form.kind === "image" || form.kind === "video") && (
              <>
                {form.kind === "video" && (
                  <div className="pt__field">
                    <span>Video (MP4 or MOV, loops muted)</span>
                    <div className="pt__inline">
                      <input value={p.video_url || ""} onChange={(e) => setP("video_url", e.target.value)} placeholder="https://" />
                      <UploadButton label="Upload video" accept="video/mp4,video/quicktime" onUploaded={(u) => setP("video_url", u)} />
                    </div>
                  </div>
                )}
                <div className="pt__field">
                  <span>{form.kind === "video" ? "Poster (first frame)" : "Image"}</span>
                  <div className="pt__inline">
                    <input
                      value={(form.kind === "video" ? p.poster_url : p.image_url) || ""}
                      onChange={(e) => setP(form.kind === "video" ? "poster_url" : "image_url", e.target.value)}
                      placeholder="https://"
                    />
                    <UploadButton label="Upload image" accept="image/jpeg,image/png,image/webp"
                      onUploaded={(u) => setP(form.kind === "video" ? "poster_url" : "image_url", u)} />
                  </div>
                </div>
                <label className="pt__field">
                  <span>Darken for legibility ({Math.round((Number(p.scrim) || 0) * 100)}%)</span>
                  <input type="range" min={0} max={0.8} step={0.02} value={Number(p.scrim) || 0} onChange={(e) => setP("scrim", Number(e.target.value))} />
                </label>
              </>
            )}

            <div className="pt__field">
              <span>Swatch (the picker tile, about 120 x 172)</span>
              <div className="pt__inline">
                <input value={form.swatch_url || ""} onChange={(e) => set("swatch_url", e.target.value)} placeholder="https://" />
                <UploadButton label="Upload swatch" accept="image/jpeg,image/png,image/webp" onUploaded={(u) => set("swatch_url", u)} />
              </div>
            </div>

            <div className="pt__inline">
              <label>
                Text tone
                <select value={form.tone} onChange={(e) => set("tone", e.target.value)}>
                  <option value="dark">Dark background, light text</option>
                  <option value="light">Light background, dark text</option>
                </select>
              </label>
              <label>
                Who sees it
                <select value={form.availability} onChange={(e) => set("availability", e.target.value)}>
                  <option value="all">Every member</option>
                  <option value="verified">Verified DJs and promoters</option>
                </select>
              </label>
              <label>
                Default title font
                <select value={form.title_font || ""} onChange={(e) => set("title_font", e.target.value)}>
                  <option value="">None</option>
                  {builtinFonts.filter((k) => {
                    // Same as the app's picker: on rows, Bold when it has no row, and the current pick.
                    const row = fonts.find((f) => f.key === k);
                    return row ? row.active || k === form.title_font : k === "classic" || k === form.title_font;
                  }).map((k) => <option key={k} value={k}>{fonts.find((f) => f.key === k)?.name || k} (built in)</option>)}
                  {fonts.filter((f) => f.active && !f.builtin).map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}
                </select>
              </label>
            </div>

            <div className="pt__field">
              <span>Face ring colors (empty uses neutral silver)</span>
              <ColorList value={form.ring_colors} min={0} max={4} onChange={(v) => set("ring_colors", v)} />
            </div>
            <div className="pt__inline">
              <label>
                "I'm in" pill
                <input type="color" value={form.join_pill.bg || "#ffffff"} onChange={(e) => set("join_pill", { ...form.join_pill, bg: e.target.value })} />
                <button type="button" className="pt__link" onClick={() => set("join_pill", { ...form.join_pill, bg: "" })}>
                  {form.join_pill.bg ? "Use ring color" : "Ring color"}
                </button>
              </label>
              <label>
                Pill text
                <input type="color" value={form.join_pill.fg || "#050505"} onChange={(e) => set("join_pill", { ...form.join_pill, fg: e.target.value })} />
                <button type="button" className="pt__link" onClick={() => set("join_pill", { ...form.join_pill, fg: "" })}>
                  {form.join_pill.fg ? "Pick by contrast" : "By contrast"}
                </button>
              </label>
              <label>
                <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} />
                In the picker
              </label>
            </div>
          </div>
          <div className="pt__form-preview">
            <ThemePreview theme={form} fallbackSwatch={fallbackSwatch} />
            <div className="pt__pill" style={{
              background: form.join_pill.bg || form.ring_colors?.[0] || "#d5d7de",
              color: form.join_pill.fg || "#050505",
            }}>I'm in</div>
          </div>
        </div>
        <div className="pt__form-footer">
          <button type="button" className="pt__secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="pt__primary" disabled={saving}>{saving ? "Saving..." : "Save"}</button>
        </div>
      </form>
    </Modal>
  );
}

function FontForm({ font, licenses, onClose, onSaved }) {
  const show = !!font;
  const isEdit = !!font?._id;
  // Built-in rows are bundled in the app: only the name and on/off are editable here.
  const builtin = !!font?.builtin;
  const [form, setForm] = useState(EMPTY_FONT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!font) return;
    setForm({
      ...EMPTY_FONT,
      ...font,
      license: { ...EMPTY_FONT.license, ...(font.license || {}) },
      title: { ...EMPTY_FONT.title, ...(font.title || {}) },
      picker_size: font.picker_size ?? "",
    });
  }, [font]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setT = (k, v) => setForm((f) => ({ ...f, title: { ...f.title, [k]: v } }));

  const save = async (e) => {
    e.preventDefault();
    if (builtin) {
      setSaving(true);
      try {
        await api.put(`/admin/plan-themes/fonts/${font.key}`, { name: form.name.trim(), active: !!form.active });
        toast.success("Font saved");
        onSaved();
      } catch (err) {
        toast.error(errMsg(err, "Could not save the font"));
      } finally {
        setSaving(false);
      }
      return undefined;
    }
    const title = {};
    ["font_size", "line_height", "letter_spacing", "max_lines"].forEach((k) => {
      if (form.title[k] !== "" && form.title[k] !== null && form.title[k] !== undefined) title[k] = Number(form.title[k]);
    });
    title.uppercase = !!form.title.uppercase;
    const body = {
      name: form.name.trim(),
      file_url: form.file_url,
      license: form.license,
      weight: String(form.weight),
      style: form.style,
      title,
      active: !!form.active,
    };
    if (form.picker_size !== "") body.picker_size = Number(form.picker_size);
    if (!isEdit) body.key = form.key.trim().toLowerCase();
    setSaving(true);
    try {
      if (isEdit) await api.put(`/admin/plan-themes/fonts/${font.key}`, body);
      else await api.post("/admin/plan-themes/fonts", body);
      toast.success(isEdit ? "Font saved" : "Font added");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err, "Could not save the font"));
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <form className="pt__form" onSubmit={save}>
        <div className="pt__form-header">
          <div className="pt__form-title">{isEdit ? `Edit ${font?.name}` : "Add font"}</div>
          <button type="button" className="pt__close" onClick={onClose} aria-label="Close"><MdClose size={20} /></button>
        </div>
        <div className="pt__form-body">
          <div className="pt__inline">
            <label className="pt__field">
              <span>Picker name</span>
              <input value={form.name} maxLength={24} onChange={(e) => set("name", e.target.value)} placeholder="Poster" />
            </label>
            <label className="pt__field">
              <span>Key</span>
              <input value={form.key} disabled={isEdit} maxLength={32} onChange={(e) => set("key", e.target.value)} placeholder="poster" />
            </label>
          </div>
          {builtin && (
            <>
              <div className="pt__note">Built in: this face is bundled in the app, so there's no file to upload.</div>
              <div className="pt__inline">
                <label><input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} />In the picker</label>
              </div>
            </>
          )}
          {!builtin && (<>
          <div className="pt__field">
            <span>Font file (TTF or OTF, uploaded unchanged)</span>
            <div className="pt__inline">
              <input value={form.file_url} readOnly placeholder="Upload the file" />
              <UploadButton label="Upload font" accept=".ttf,.otf" onUploaded={(u) => set("file_url", u)} />
            </div>
          </div>
          <div className="pt__inline">
            <label>
              License
              <select value={form.license.name} onChange={(e) => set("license", { ...form.license, name: e.target.value })}>
                {licenses.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </label>
            <label className="pt__grow">
              License text link
              <input value={form.license.url} onChange={(e) => set("license", { ...form.license, url: e.target.value })} placeholder="https://github.com/google/fonts/blob/main/ofl/.../OFL.txt" />
            </label>
          </div>
          <div className="pt__inline">
            <label>
              Weight
              <select value={form.weight} onChange={(e) => set("weight", e.target.value)}>
                {["100", "200", "300", "400", "500", "600", "700", "800", "900"].map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </label>
            <label>
              Style
              <select value={form.style} onChange={(e) => set("style", e.target.value)}>
                <option value="normal">Normal</option>
                <option value="italic">Italic</option>
              </select>
            </label>
          </div>
          <div className="pt__note">Title fit, for the app's 32pt title. Leave blank to use the defaults.</div>
          <div className="pt__inline">
            <label>Size<input type="number" min={16} max={72} value={form.title.font_size} onChange={(e) => setT("font_size", e.target.value)} /></label>
            <label>Line height<input type="number" min={16} max={90} value={form.title.line_height} onChange={(e) => setT("line_height", e.target.value)} /></label>
            <label>Letter spacing<input type="number" min={-4} max={8} step={0.1} value={form.title.letter_spacing} onChange={(e) => setT("letter_spacing", e.target.value)} /></label>
            <label>Warn past lines<input type="number" min={1} max={4} value={form.title.max_lines} onChange={(e) => setT("max_lines", e.target.value)} /></label>
            <label>Picker size<input type="number" min={10} max={28} value={form.picker_size} onChange={(e) => set("picker_size", e.target.value)} /></label>
            <label><input type="checkbox" checked={!!form.title.uppercase} onChange={(e) => setT("uppercase", e.target.checked)} />Uppercase</label>
            <label><input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} />In the picker</label>
          </div>
          {form.file_url && (
            <div className="pt__font-preview">
              <FontSample font={form} text={SAMPLE_TITLE} />
              <FontSample font={form} text={form.name || "Name"} size={Number(form.picker_size) || 17} />
            </div>
          )}
          </>)}
        </div>
        <div className="pt__form-footer">
          <button type="button" className="pt__secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="pt__primary" disabled={saving}>{saving ? "Saving..." : "Save"}</button>
        </div>
      </form>
    </Modal>
  );
}

export default PlanThemes;
